/**
 * 全局样式管理器 - 将消息级 CSS 注入 document.head 并持久化
 * 通过 Set 记录已注入的 msgId，避免重复注入
 *
 * 对于包含 @import 的 CSS，会分离处理：
 * - CSS rules 立即注入（避免阻塞）
 * - @import 延迟 100ms 注入（避免阻塞渲染）
 */

const injectedStyles = new Set<string>()
const injectedImports = new Set<string>()
const STYLE_MARKER_ATTR = 'data-chat-dom-global-style'

interface StyleEntry {
  rulesEl?: HTMLStyleElement
  importsEl?: HTMLStyleElement
  importTimer?: number
}

const styleRegistry = new Map<string, StyleEntry>()

/**
 * Extract @import statements from CSS
 * @returns { imports: @import statements, rules: remaining CSS }
 */
function extractImports(css: string): { imports: string; rules: string } {
  const imports: string[] = []
  const rules = css.replace(
    /@import\s+(?:url\s*\([^)]+\)|['"][^'"]+['"])[^;]*;/gi,
    (match) => {
      imports.push(match)
      return ''
    }
  )
  return {
    imports: imports.join('\n'),
    rules: rules.trim(),
  }
}

export function ensureGlobalStyles(msgId: string, css: string): void {
  if (injectedStyles.has(msgId)) return

  const { imports, rules } = extractImports(css)
  const entry = styleRegistry.get(msgId) ?? {}

  // Immediately inject rules (non-blocking)
  if (rules) {
    const rulesStyle = document.createElement('style')
    rulesStyle.setAttribute(STYLE_MARKER_ATTR, '1')
    rulesStyle.setAttribute('data-msg-id', msgId)
    rulesStyle.textContent = rules
    document.head.appendChild(rulesStyle)
    entry.rulesEl = rulesStyle
  }

  // Delay inject @import to avoid blocking CSS parsing
  if (imports && !injectedImports.has(msgId)) {
    const timerId = window.setTimeout(() => {
      const activeEntry = styleRegistry.get(msgId)
      if (!activeEntry) return

      const importStyle = document.createElement('style')
      importStyle.setAttribute(STYLE_MARKER_ATTR, '1')
      importStyle.setAttribute('data-msg-id', `${msgId}-imports`)
      importStyle.textContent = imports
      // Insert at the beginning to ensure @import is before other rules
      document.head.insertBefore(importStyle, document.head.firstChild)
      activeEntry.importsEl = importStyle
      activeEntry.importTimer = undefined
      injectedImports.add(msgId)
    }, 100)
    entry.importTimer = timerId
  }

  styleRegistry.set(msgId, entry)
  injectedStyles.add(msgId)
}

export function clearGlobalStyles(): void {
  for (const entry of styleRegistry.values()) {
    if (entry.importTimer !== undefined) {
      window.clearTimeout(entry.importTimer)
    }
    entry.rulesEl?.remove()
    entry.importsEl?.remove()
  }

  // Fallback cleanup in case any style node was detached from registry state.
  document
    .querySelectorAll<HTMLStyleElement>(`style[${STYLE_MARKER_ATTR}="1"]`)
    .forEach((node) => node.remove())

  styleRegistry.clear()
  injectedStyles.clear()
  injectedImports.clear()
}
